// EmailUI: 07.07.2026 Bappi

import React, { useContext, useEffect, useState, useRef } from "react";
import { AnimatePresence, m, motion } from "framer-motion";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { UrlContext } from "../../contexts/UrlContext.jsx";
import { UserContext } from "../../contexts/UserContext.jsx";
import Quill from "quill";
import scrollDesign from "../../components/EphProfile/MyProjectPage.module.css";
import Swal from "sweetalert2";
import { te } from "date-fns/locale";
import { set } from "date-fns";

const TemplateBlock = Quill.import("blots/block");
TemplateBlock.tagName = "div";
Quill.register(TemplateBlock, true);

const TemplateFont = Quill.import("formats/font");
TemplateFont.whitelist = [
  "arial",
  "georgia",
  "times-new-roman",
  "courier-new",
  "verdana",
  "tahoma",
  "lucida",
];
Quill.register(TemplateFont, true);

export default function EmailTemplate({
  project_name,
  project_id,
  setShowEmailTemplate,
}) {
  const [template, setTemplate] = useState("Email");
  const [createNew, setCreateNew] = useState(false);

  const currentTypeLabel = getTemplateTypeLabel(template);

  const openCreateNew = () => {
    setCreateNew(true);
  };

  const backToTemplates = (targetType = template) => {
    setTemplate(targetType || template);
    setCreateNew(false);
  };

  const handleCreatedTemplate = (createdType = template) => {
    setTemplate(createdType || template);
    setCreateNew(false);
  };

  // Block body scroll when modal is open
  useEffect(() => {
    document.body.classList.add("overflow-hidden");
    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, []);

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-x-0 bottom-0 top-[78px] z-50 flex items-center justify-center px-2 py-3 sm:px-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        <div
          className="absolute inset-0 bg-black/45 backdrop-blur-sm"
          onClick={() => setShowEmailTemplate(false)}
        />

        <motion.div
          layout
          initial={{ y: 18, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 18, opacity: 0 }}
          transition={{ type: "spring", stiffness: 320, damping: 28 }}
          className="relative flex h-[calc(95vh-78px)] w-full max-w-7xl flex-col overflow-hidden rounded-3xl bg-[radial-gradient(circle_at_top,rgba(54,85,97,0.18),transparent_35%),linear-gradient(to_bottom,#f8fafc,#eef2f6)] shadow-[0_24px_80px_rgba(15,23,42,0.30)]"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="shrink-0 border-b border-slate-200/80 bg-transparent px-4 py-4 sm:px-5">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#365561]">
                  Template manager
                </p>
                <h2 className="mt-1 truncate text-xl font-semibold text-slate-900 sm:text-2xl">
                  {createNew ? `Create ${currentTypeLabel} Template` : "Email Templates"}
                </h2>
                <p className="mt-1 line-clamp-2 max-w-[800px] text-sm text-slate-500">
                  Manage project templates for {project_name}. Review shared templates, copy useful versions, edit your own templates, and choose your default template.
                </p>
              </div>

              <button
                type="button"
                className="group relative inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:bg-slate-50 hover:text-red-500 focus:outline-none"
                aria-label="Close template manager"
                onClick={() => setShowEmailTemplate(false)}
              >
                <i className="fa-solid fa-xmark text-base" />
                <span className="pointer-events-none absolute right-0 top-full z-50 mt-2 hidden whitespace-nowrap rounded-lg bg-slate-900 px-2 py-1 text-xs font-medium text-white shadow-lg group-hover:block">
                  Close
                </span>
              </button>
            </div>
          </div>

          <div className="shrink-0 px-4 py-3 sm:px-5">
            <div className="flex flex-wrap items-center gap-2">
              <TemplateTypeToggle value={template} onChange={setTemplate} />

              {createNew ? (
                <IconExpandButton
                  icon="fa-arrow-left"
                  label={`Back to ${currentTypeLabel} templates`}
                  onClick={() => backToTemplates(template)}
                  className="border border-slate-200 bg-white text-slate-600 shadow-sm hover:bg-slate-50"
                  iconClassName="bg-white text-slate-600"
                  labelClassName="text-slate-600"
                />
              ) : (
                <IconExpandButton
                  icon="fa-plus"
                  label="Create new template"
                  onClick={openCreateNew}
                  className="bg-[#365561] text-white shadow-[0_14px_30px_rgba(54,85,97,0.24)] hover:bg-[#2c4650] hover:shadow-[0_18px_38px_rgba(54,85,97,0.28)]"
                  iconClassName="bg-transparent text-white"
                  labelClassName="text-white"
                />
              )}
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3 sm:px-4 lg:overflow-hidden lg:px-5 lg:py-4">
            {createNew ? (
              <CreateNew
                key={`create-template-${template}`}
                project_id={project_id}
                initialTemplateType={template}
                setTemplate={setTemplate}
                onBack={backToTemplates}
                onCreated={handleCreatedTemplate}
                setCreateNew={setCreateNew}
              />
            ) : (
              <>
                {template === "Email" && (
                  <OutreachEmail project_id={project_id} />
                )}
                {template === "FollowUp" && (
                  <FollowupEmail project_id={project_id} />
                )}
                {template === "Invite" && (
                  <InviteEmail project_id={project_id} />
                )}
                {template === "Connection" && (
                  <ConnectionNote project_id={project_id} />
                )}
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

const TEMPLATE_TYPE_OPTIONS = [
  {
    value: "Email",
    label: "First Outreach",
    icon: "fa-envelope",
    activeBg: "bg-[#365561]",
    inactiveText: "text-[#365561]",
    hoverBg: "hover:bg-[#365561]/10",
  },
  {
    value: "Invite",
    label: "Invite",
    icon: "fa-paper-plane",
    activeBg: "bg-blue-500",
    inactiveText: "text-blue-700",
    hoverBg: "hover:bg-blue-50",
  },
  {
    value: "FollowUp",
    label: "Follow-up",
    icon: "fa-reply",
    activeBg: "bg-emerald-600",
    inactiveText: "text-emerald-700",
    hoverBg: "hover:bg-emerald-50",
  },
  {
    value: "Connection",
    label: "Connection",
    icon: "fa-linkedin",
    activeBg: "bg-slate-700",
    inactiveText: "text-slate-700",
    hoverBg: "hover:bg-slate-100",
    brand: true,
  },
];

function getTemplateTypeLabel(value) {
  return TEMPLATE_TYPE_OPTIONS.find((option) => option.value === value)?.label || "Template";
}

function getLanguageBadge(language = "") {
  const normalized = String(language || "").toLowerCase();

  if (normalized.includes("german") || normalized === "de") return "DE";
  if (normalized.includes("english") || normalized === "en") return "EN";

  return String(language || "").slice(0, 2).toUpperCase() || "--";
}

function IconExpandButton({
  icon,
  label,
  onClick,
  className = "",
  iconClassName = "",
  labelClassName = "",
  disabled = false,
  type = "button",
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={[
        "group relative inline-flex h-9 shrink-0 items-center overflow-hidden rounded-full transition-all duration-200 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 sm:h-10",
        className,
      ].join(" ")}
      aria-label={label}
      title={label}
    >
      <span
        className={[
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-full sm:h-10 sm:w-10",
          iconClassName,
        ].join(" ")}
      >
        <i className={["fa-solid", icon, "text-sm"].join(" ")} />
      </span>
      <span
        className={[
          "max-w-0 whitespace-nowrap pr-0 text-xs font-semibold opacity-0 transition-all duration-200 group-hover:max-w-[230px] group-hover:pr-5 group-hover:opacity-100 sm:text-sm",
          labelClassName,
        ].join(" ")}
      >
        {label}
      </span>
      <span className="pointer-events-none absolute left-1/2 top-full z-50 mt-2 hidden -translate-x-1/2 whitespace-nowrap rounded-lg bg-slate-900 px-2 py-1 text-xs font-medium text-white shadow-lg group-hover:hidden">
        {label}
      </span>
    </button>
  );
}

function TemplateTypeToggle({ value, onChange }) {
  return (
    <div className="relative inline-flex w-auto max-w-full overflow-x-auto rounded-2xl border border-slate-200 bg-white p-1 shadow-[0_10px_30px_rgba(15,23,42,0.05)]">
      {TEMPLATE_TYPE_OPTIONS.map((option) => {
        const isActive = value === option.value;

        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={[
              "relative z-10 inline-flex shrink-0 items-center gap-1 rounded-xl px-2 py-1.5 text-[10px] font-semibold transition-colors duration-200 focus:outline-none sm:gap-2 sm:px-3 sm:text-xs",
              isActive ? "text-white" : `${option.inactiveText} ${option.hoverBg}`,
            ].join(" ")}
          >
            {isActive && (
              <motion.span
                layoutId="template-type-toggle-pill"
                className={[
                  "absolute inset-0 -z-10 rounded-xl shadow-[0_10px_24px_rgba(54,85,97,0.18)]",
                  option.activeBg,
                ].join(" ")}
                transition={{ type: "spring", stiffness: 420, damping: 32 }}
              />
            )}

            <i className={`${option.brand ? "fa-brands" : "fa-solid"} ${option.icon} text-[11px] sm:text-[13px]`} />
            <span>{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function TemplateScopeToggle({ isIncludeAll, setIsIncludeAll, myTemplateCount = 0 }) {
  const hasMyTemplates = myTemplateCount > 0;
  const options = [
    { value: true, label: "All templates" },
    { value: false, label: "My templates", count: myTemplateCount },
  ];

  return (
    <div className="mb-3 relative grid grid-cols-2 rounded-2xl border border-slate-200 bg-slate-50 p-1">
      {options.map((option) => {
        const isActive = isIncludeAll === option.value;
        const isDisabled = option.value === false && !hasMyTemplates;

        return (
          <button
            key={String(option.value)}
            type="button"
            disabled={isDisabled}
            onClick={() => {
              if (!isDisabled) setIsIncludeAll(option.value);
            }}
            className={[
              "relative z-10 rounded-xl px-2 py-1.5 text-[10px] font-semibold transition-colors duration-200 focus:outline-none disabled:cursor-not-allowed disabled:opacity-45 sm:px-3 sm:text-xs",
              isActive ? "text-white" : "text-slate-500 hover:text-slate-800",
              isDisabled ? "hover:text-slate-500" : "",
            ].join(" ")}
            title={isDisabled ? "No templates saved under your account yet" : option.label}
          >
            {isActive && (
              <motion.span
                layoutId="template-scope-toggle-pill"
                className="absolute inset-0 -z-10 rounded-xl bg-[#365561] shadow-sm"
                transition={{ type: "spring", stiffness: 420, damping: 32 }}
              />
            )}
            {option.label}
            {option.count > 0 && (
              <span className="ml-1 text-[10px] opacity-80">({option.count})</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

const LANGUAGE_CATEGORY_OPTIONS = [
  { value: "English", label: "EN" },
  { value: "German - formal", label: "DE - formal" },
  { value: "German - informal", label: "DE - informal" },
];

function LanguageCategoryToggle({ value, onChange }) {
  return (
    <div className="relative inline-flex w-auto max-w-full overflow-x-auto rounded-2xl border border-slate-200 bg-slate-50 p-1">
      {LANGUAGE_CATEGORY_OPTIONS.map((option) => {
        const isActive = value === option.value;

        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={[
              "relative z-10 inline-flex shrink-0 items-center justify-center rounded-xl px-2.5 py-1 text-[10px] font-semibold transition-colors duration-200 focus:outline-none sm:px-3 sm:text-xs",
              isActive ? "text-white" : "text-slate-500 hover:bg-white hover:text-slate-800",
            ].join(" ")}
          >
            {isActive && (
              <motion.span
                layoutId="language-category-toggle-pill"
                className="absolute inset-0 -z-10 rounded-xl bg-[#365561] shadow-sm"
                transition={{ type: "spring", stiffness: 420, damping: 32 }}
              />
            )}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}


async function fetchProjectDetailsForTemplate(API_URL, projectID) {
  try {
    const response = await fetch(`${API_URL}get-project-details-for-template`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ projectID }),
    });

    const responseData = await response.json();
    if (responseData.status === "success") {
      return responseData.projectData || {};
    }
  } catch (error) {
    console.error("Failed to fetch project details for fallback templates:", error);
  }

  return {};
}

function formatTemplateStartDate(project = {}, lang = "en") {
  if (!project?.projStartDate) return lang === "de" ? "Noch offen" : "TBD";

  const startDate = new Date(project.projStartDate);
  if (Number.isNaN(startDate.getTime())) return lang === "de" ? "Noch offen" : "TBD";

  return startDate.toLocaleDateString(lang === "de" ? "de-DE" : "en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function buildFallbackTemplateItems(templateType, project = {}) {
  const generateOutreachTemplates = (project, cons_exp) => {
    const buildDetails = (lang) => {
      const startDate = new Date(project.projStartDate);
      const lines =
        lang === "en"
          ? [
              project.description,
              "Details:",
              `Project Start Date: ${startDate.toLocaleDateString("en-GB", {
                weekday: "short",
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}`,
              `Project Duration: ${project.projDuration}`,
              `Work mode: ${project.empType}`,
              `Industry: ${project.industry}`,
            ]
          : [
              project.description,
              "Einzelheiten:",
              `Projektstartdatum: ${startDate.toLocaleDateString("de-DE", {
                weekday: "short",
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}`,
              `Projektdauer: ${project.projDuration}`,
              `Arbeitsmodus: ${project.empType}`,
              `Industrie: ${project.industry}`,
            ];

      if (cons_exp === "exp") {
        lines.push(
          lang === "en"
            ? "This would be an attractively paid 1-hour discussion by phone."
            : "Dabei handelt es sich um ein attraktiv bezahltes 1-stündiges Telefongespräch."
        );
      }
      return lines.join("<br />");
    };

    return [
      {
        lang: "en",
        label: "English",
        name: "Outreach – EN default",
        subject: `expertpowerhouse | ${project.projectName}`,
        value: `
          <div>Dear first_name,</div>
          <div><br></div>
          <div>As we have not yet been in contact please allow me to introduce myself. My name is user_name from expertpowerhouse GmbH - an agency matching experts and projects globally.</div>
          <div><br></div>
          ${buildDetails("en")}
          <div><br></div>
          <div>Would that be of interest to you at all?</div>
          <div><br></div>
          <div>I would in any case be very happy to receive a brief reply.</div>
          <div><br></div>
          <p>Best regards</p>
          <p>first_user</p>
        `,
      },
      {
        lang: "de",
        label: "German - formal",
        name: "Outreach – DE formal",
        subject: `expertpowerhouse | ${project.projectName}`,
        value: `
          <div>Sehr geehrte/r Herr/Frau last_name,</div>
          <div><br></div>
          <div>da wir noch keinen Kontakt hatten, gestatten Sie mir bitte, mich vorzustellen. Mein Name ist user_name von der expertpowerhouse GmbH - einer Agentur, die weltweit Experten und Projekte vermittelt.</div>
          <div><br></div>
          ${buildDetails("de")}
          <div><br></div>
          <div>Wäre das für Sie überhaupt interessant?</div>
          <div><br></div>
          <div>Über eine kurze Antwort würde ich mich jedenfalls sehr freuen.</div>
          <div><br></div>
          <div>Mit freundlichen Grüßen</div>
          <div>user_name</div>
        `,
      },
      {
        lang: "de",
        label: "German - informal",
        name: "Outreach – DE informal",
        subject: `expertpowerhouse | ${project.projectName}`,
        value: `
          <div>Hallo first_name,</div>
          <div><br></div>
          <div>da wir noch keinen Kontakt hatten, gestatte mir bitte, mich vorzustellen. Mein Name ist user_name von der expertpowerhouse GmbH - einer Agentur, die weltweit Experten und Projekte vermittelt.</div>
          <div><br></div>
          ${buildDetails("de")}
          <div><br></div>
          <div>Wäre das für dich überhaupt interessant?</div>
          <div><br></div>
          <div>Über eine kurze Antwort würde ich mich jedenfalls sehr freuen.</div>
          <div><br></div>
          <div>Viele Grüße</div>
          <div>first_user</div>
        `,
      },
    ];
  };

  const generateInviteTemplates = (projectName, projectDescription) => [
    {
      lang: "en",
      label: "English",
      name: "Project Invitation - EN default",
      subject: `expertpowerhouse | Invitation to Project Opportunity: ${projectName}`,
      value: `
        <div>Dear first_name,</div>
        <div><br></div>
        <div>We believe you could be a great fit for the following project opportunity:</div>
        <div><br></div>
        <blockquote style="padding: 10px 16px; background-color: #f9f9f9; border-left: 4px solid #365561; font-style: italic;">
          <strong>"${projectName}"</strong><br>
          ${projectDescription}
        </blockquote>
        <div><br></div>
        <div>If you are available and interested, please click the button below to access your consultant profile. Once there, navigate to the "Invitations" tab to express your interest.</div>
        {{BUTTON_DO_NOT_REMOVE}}
        <div><br></div>
        <div>Best regards,</div>
        <div>first_user</div>
      `,
    },
    {
      lang: "de",
      label: "German - formal",
      name: "Project Invitation - DE formal",
      subject: `expertpowerhouse | Einladung zur Projektchance: ${projectName}`,
      value: `
        <div>Sehr geehrte/r Herr/Frau last_name,</div>
        <div><br></div>
        <div>ich hoffe Ihnen geht es gut! Ich glaube, dass Sie sehr gut zu folgendem Projekt passen könnten:</div>
        <div><br></div>
        <blockquote style="padding: 10px 16px; background-color: #f9f9f9; border-left: 4px solid #365561; font-style: italic;">
          <strong>"${projectName}"</strong><br>
          ${projectDescription}
        </blockquote>
        <div><br></div>
        <div>Wenn Sie verfügbar und interessiert sind, können Sie direkt über den Button unten Ihr Beraterprofil aufrufen, um dort im Bereich "Invitations" Ihr Interesse zu bestätigen.</div>
        {{BUTTON_DO_NOT_REMOVE}}
        <div><br></div>
        <div>Mit freundlichen Grüßen</div>
        <div>user_name</div>
      `,
    },
    {
      lang: "de",
      label: "German - informal",
      name: "Project Invitation - DE informal",
      subject: `expertpowerhouse | Einladung zur Projektchance: ${projectName}`,
      value: `
        <div>Hallo first_name,</div>
        <div><br></div>
        <div>ich hoffe dir geht es gut! Ich glaube, dass du sehr gut zu folgendem Projekt passen könntest:</div>
        <div><br></div>
        <blockquote style="padding: 10px 16px; background-color: #f9f9f9; border-left: 4px solid #365561; font-style: italic;">
          <strong>"${projectName}"</strong><br>
          ${projectDescription}
        </blockquote>
        <div><br></div>
        <div>Wenn du verfügbar und interessiert bist kannst du direkt über den Button unten dein Beraterprofil aufrufen, um dort im Bereich "Invitations" dein interesse zu bestätigen.</div>
        {{BUTTON_DO_NOT_REMOVE}}
        <div><br></div>
        <div>Viele Grüße,</div>
        <div>first_user</div>
      `,
    },
  ];

  const generateFollowUpTemplates = (projectName, projectDescription, cons_exp) => [
    {
      lang: "en",
      label: "English",
      name: "Follow-Up - EN",
      subject: `expertpowerhouse | Follow-up regarding our previous conversation: ${projectName}`,
      value: cons_exp === "exp" ? 
      `
        <div>Hi first_name,</div>
        <div><br></div>
        <div>I just wanted to quickly follow up and see if you had a chance to review my previous message?</div>
        <div><br></div>
        <div>Kindly let me know if you have any questions or concerns! Our client operates on a tight timeline - if you'd have a moment to respond today that would be highly appreciated! Also happy to give you a call whenever convenient.</div>
        <div><br></div>
        <div>Kind regards,</div>
        <div>user_name</div>
      ` :
      `
        <div>Dear first_name,</div>
        <div><br></div>
        <div>Thank you very much for your interest in this engagement. As previously mentioned, the project concerns the following:</div>
        <div><br></div>
        <blockquote style="padding: 10px 16px; background-color: #f9f9f9; border-left: 4px solid #365561; font-style: italic;">
          <strong>"${projectName}"</strong><br>
          ${projectDescription}
        </blockquote>
        <div><br></div>
        <div>If the project remains of interest to you, it would be greatly appreciated if you could share a few sentences outlining your relevant experience. This will help us position you in the best possible way with the client. In addition, could you please let us know your expected daily rate for this project?</div>
        <div><br></div>
        <div>If you have any questions or require further information, I would be happy to assist you.</div>
        <div>We would also be glad to arrange a short call at your convenience.</div>
        <div><br></div>
        <div>Kind regards,</div>
        <div>first_user</div>
      `,
    },
    {
      lang: "de",
      label: "German - formal",
      name: "Follow-Up - DE formal",
      subject: `expertpowerhouse | Nachfrage zu unserer vorherigen Nachricht: ${projectName}`,
      value: cons_exp === "exp" ? 
      `
        <div>Guten Tag Herr/Frau last_name,</div> 
        <div><br></div> 
        <div>ich wollte nur kurz nachfragen, ob Sie die Möglichkeit hatten, meine vorherige Nachricht zu lesen?</div> 
        <div><br></div> 
        <div>Bitte lassen Sie mich wissen, wenn Sie noch Fragen oder Bedenken haben! Unser Kunde hat hier etwas Zeitdruck. Wenn Sie also weiterhin Interesse haben, wäre es klasse, wenn Sie mir heute antworten könnten. Für eine Rückmeldung wäre ich Ihnen jedenfalls sehr dankbar! Ich rufe Sie auch gerne an, wenn es Ihnen passt.</div> 
        <div><br></div> 
        <div>Mit freundlichen Grüßen</div> 
        <div>user_name</div>
      ` :
      `
        <div>Guten Tag Herr/Frau last_name,</div>
        <div><br></div>
        <div>vielen Dank für Ihr Interesse an dem Engagement. Wie bereits geschrieben geht es um folgendes:</div>
        <div><br></div>
        <blockquote style="padding: 10px 16px; background-color: #f9f9f9; border-left: 4px solid #365561; font-style: italic;">
          <strong>"${projectName}"</strong><br>
          ${projectDescription}
        </blockquote>
        <div><br></div>
        <div>Falls das Projekt weiterhin für Sie interessant ist, wäre es sehr hilfreich, wenn Sie mir ein paar Sätze mit Ihrer relevanten Erfahrung mitteilen könnten. Das hilft uns, Sie bestmöglich beim Kunden zu positionieren. Außerdem: was ist Ihre Tagessatzerwartung für dieses Projekt?</div>
        <div><br></div>
        <div>Wenn Sie noch Fragen haben oder weitere Informationen benötigen, stehe ich Ihnen selbstverständlich gerne zur Verfügung.</div>
        <div>Gerne können wir auch kurzfristig telefonieren, falls das für Sie einfacher ist.</div>
        <div><br></div>
        <div>Mit freundlichen Grüßen</div>
        <div>user_name</div>
      `,
    },
    {
      lang: "de",
      label: "German - informal",
      name: "Follow-Up Email - DE informal",
      subject: `expertpowerhouse | Nachfrage zu unserer vorherigen Nachricht: ${projectName}`,
      value: cons_exp === "exp" ? 
      `
        <div>Guten Tag first_name,</div> 
        <div><br></div> 
        <div>ich wollte nur kurz nachfragen, ob Du die Möglichkeit hattest, meine vorherige Nachricht zu lesen?</div> 
        <div><br></div> 
        <div>Bitte lass mich wissen, wenn Du noch Fragen oder Bedenken hast! Unser Kunde hat hier etwas Zeitdruck. Wenn Du also weiterhin Interesse hast, wäre es klasse, wenn Du mir heute antworten könntest. Für eine Rückmeldung wäre ich Dir jedenfalls sehr dankbar! Ich rufe Dich auch gerne an, wenn es Dir passt.</div> 
        <div><br></div> 
        <div>Mit freundlichen Grüßen</div> 
        <div>first_user</div>
      ` :
      `
        <div>Hallo first_name,</div>
        <div><br></div>
        <div>vielen Dank für dein Interesse an dem Projekt. Wie bereits geschrieben geht es um folgendes:</div>
        <div><br></div>
        <blockquote style="padding: 10px 16px; background-color: #f9f9f9; border-left: 4px solid #365561; font-style: italic;">
          <strong>"${projectName}"</strong><br>
          ${projectDescription}
        </blockquote>
        <div><br></div>
        <div>Falls das Projekt weiterhin für dich interessant ist, wäre es sehr hilfreich, wenn du mir ein paar Sätze mit deiner relevanten Erfahrung mitteilen könntest. Das hilft uns, dich bestmöglich beim Kunden zu positionieren. Außerdem: was ist deine Tagessatzerwartung für dieses Projekt?</div>
        <div><br></div>
        <div>Wenn du noch Fragen hast oder weitere Informationen benötigst, stehe ich dir selbstverständlich gerne zur Verfügung.</div>
        <div>Gerne können wir auch kurzfristig telefonieren, falls das für dich einfacher ist.</div>
        <div><br></div>
        <div>Viele Grüße</div>
        <div>first_user</div>
      `,
    },
  ];

  const generateInMailTemplates = (projectName) => [
    {
      lang: "en",
      label: "English",
      name: "LinkedIn InMail Outreach - EN",
      subject: "",
      value: `
  Dear first_name,

I am user_name from expertpowerhouse, a firm that matches global projects and consultants. Currently, I have one project related to ${projectName} and would like to discuss it with you. Shall we connect to discuss further?

Best regards,
user_name
    `.trim(),
    },
    {
      lang: "de",
      label: "German - formal",
      name: "LinkedIn InMail Outreach - DE formal",
      subject: "",
      value: `
  Hallo Herr/Frau last_name,

ich bin user_name von expertpowerhouse, einem Unternehmen, das globale Projekte und Berater zusammenbringt. Derzeit habe ich ein Projekt im Zusammenhang mit ${projectName} und möchte es mit Ihnen besprechen. Sollen wir uns vernetzen, um weitere Gespräche zu führen?

Beste Grüße,
user_name
    `.trim(),
    },
    {
      lang: "de",
      label: "German - informal",
      name: "LinkedIn InMail Outreach - DE informal",
      subject: "",
      value: `
  Liebe/r first_name,

ich bin user_name von expertpowerhouse, einem Unternehmen, das globale Projekte und Berater zusammenbringt. Derzeit habe ich ein Projekt im Zusammenhang mit ${projectName} und möchte es mit dir besprechen. Sollen wir uns vernetzen, um weitere Gespräche zu führen?

Beste Grüße,
user_name
    `.trim(),
    },
  ];

  const templateMap = {
    Email: generateOutreachTemplates(project, project?.cons_exp),
    Invite: generateInviteTemplates(project?.projectName, project?.description),
    FollowUp: generateFollowUpTemplates(project?.projectName, project?.description, project?.cons_exp),
    Connection: generateInMailTemplates(project?.projectName),
  };

  return (templateMap[templateType] || templateMap.Email).map((template, index) => ({
    id: `fallback-${templateType}-${index}`,
    name: template.name,
    language: template.label,
    subject: template.subject,
    body: template.value,
    created_by: "expertpowerhouse default",
    created_date: "Default",
    isDefault: false,
    isSystemDefault: true,
  }));
}

function TemplateManagerWorkspace({
  title,
  emptyMessage,
  loading,
  allTemplates = [],
  templateList = [],
  selectedTemplate,
  onSelectTemplate,
  isIncludeAll,
  setIsIncludeAll,
  userName,
  language,
  subject,
  body,
  setSubject,
  setBody,
  isConnection = false,
  creatingNewExisting = false,
  onCopyToMine,
  onCopyAndSetDefault,
  onSaveChanges,
  onSaveAndSetDefault,
  onSetDefault,
  onReviewTemplate,
}) {
  const myTemplateCount = allTemplates.filter(
    (tpl) => tpl.created_by === userName && !tpl.isSystemDefault
  ).length;

  useEffect(() => {
    if (!isIncludeAll && myTemplateCount === 0) {
      setIsIncludeAll(true);
    }
  }, [isIncludeAll, myTemplateCount, setIsIncludeAll]);
  const selectedIsSystemDefault = Boolean(selectedTemplate?.isSystemDefault);
  const selectedIsMine = selectedTemplate?.created_by === userName;
  const selectedIsDefault = Boolean(selectedTemplate?.isDefault);
  const hasTemplateChanges = Boolean(selectedTemplate) && (
    String(subject || "") !== String(selectedTemplate?.subject || "") ||
    String(body || "") !== String(selectedTemplate?.body || "")
  );
  const languageBadge = getLanguageBadge(language || selectedTemplate?.language);

  const selectTemplateById = (templateID) => {
    if (templateID === null || templateID === undefined || templateID === "") return;
    onSelectTemplate?.(templateID);
  };

  if (loading) {
    return (
      <div className="flex h-full min-h-[420px] items-center justify-center rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="text-center">
          <i className="fa-solid fa-spinner fa-spin text-4xl text-[#365561]" />
          <p className="mt-3 text-sm font-medium text-slate-500">Loading templates...</p>
        </div>
      </div>
    );
  }

  if (!templateList.length) {
    return (
      <div className="flex h-full min-h-[420px] items-center justify-center rounded-3xl border border-dashed border-slate-300 bg-white px-5 text-center shadow-sm">
        <div>
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-3xl bg-[#365561]/10 text-[#365561]">
            <i className="fa-regular fa-envelope text-2xl" />
          </div>
          <p className="mt-3 text-sm font-semibold text-slate-700">No templates found</p>
          <p className="mt-1 max-w-md text-sm text-slate-500">{emptyMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid h-full min-h-0 grid-cols-1 gap-4 lg:grid-cols-[340px_minmax(0,1fr)]">
      <aside className="flex min-h-[360px] flex-col rounded-3xl border border-slate-200/80 bg-white p-4 shadow-[0_10px_30px_rgba(15,23,42,0.06)] lg:min-h-0">
        <div className="mb-4">
          <p className="text-base font-semibold text-slate-900">Template Library</p>
          <p className="mt-1 text-sm text-slate-500">{title} templates for this project</p>
        </div>

        <TemplateScopeToggle
          isIncludeAll={isIncludeAll}
          setIsIncludeAll={setIsIncludeAll}
          myTemplateCount={myTemplateCount}
        />

        <div className={`${scrollDesign.scrollbarOverlay} min-h-0 flex-1 space-y-2 overflow-y-auto pr-1`}>
          {templateList.map((tpl) => {
            const isSelected = selectedTemplate?.id === tpl.id;
            const isMine = tpl.created_by === userName;

            return (
              <button
                key={tpl.id}
                type="button"
                onClick={() => selectTemplateById(tpl.id)}
                className={[
                  "w-full rounded-2xl border p-3 text-left transition focus:outline-none",
                  isSelected
                    ? "border-[#365561]/35 bg-[#365561]/10 shadow-[0_12px_30px_rgba(54,85,97,0.10)]"
                    : "border-slate-200 bg-white hover:border-[#365561]/25 hover:bg-slate-50",
                ].join(" ")}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-900">
                    {tpl.name || "Untitled template"}
                  </p>
                  {tpl.isDefault && (
                    <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                      Default
                    </span>
                  )}
                  {tpl.isSystemDefault && (
                    <span className="shrink-0 rounded-full bg-[#365561]/10 px-2 py-0.5 text-[10px] font-bold text-[#365561]">
                      Pre-filled
                    </span>
                  )}
                </div>

                <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px] font-medium text-slate-500">
                  <span className={isMine ? "text-[#365561]" : ""}>
                    {isMine ? "Mine" : tpl.isSystemDefault ? "Default library" : tpl.created_by || "Shared"}
                  </span>
                  {tpl.created_date && <span>• {tpl.created_date}</span>}
                  {tpl.language && <span>• {tpl.language}</span>}
                </div>
              </button>
            );
          })}
        </div>
      </aside>

      <section className="flex min-h-[560px] min-w-0 flex-col overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.06)] lg:min-h-0">
        <div className="shrink-0 border-b border-slate-200 px-4 py-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="truncate text-base font-semibold text-slate-900">
                  {selectedTemplate?.name || "Selected template"}
                </h3>
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-600">
                  {languageBadge}
                </span>
                {selectedIsDefault && (
                  <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-bold text-emerald-700">
                    Default
                  </span>
                )}
                {selectedIsSystemDefault && (
                  <span className="rounded-full bg-[#365561]/10 px-2 py-0.5 text-xs font-bold text-[#365561]">
                    Pre-filled default
                  </span>
                )}
                {!selectedIsMine && !selectedIsSystemDefault && (
                  <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-bold text-amber-700">
                    Shared
                  </span>
                )}
              </div>
              <p className="mt-1 text-xs text-slate-500">
                {selectedIsMine
                  ? "Edit your own template here. Default selection is controlled separately."
                  : selectedIsSystemDefault
                    ? "This pre-filled default template is ready to copy into your templates and set as your default."
                    : "This is a shared template. You can review it, copy it, or copy it and set it as default."}
              </p>
            </div>
          </div>
        </div>

        <div className={`${scrollDesign.scrollbarOverlay} min-h-0 flex-1 overflow-y-auto px-4 py-4`}>
          <div className="shrink-0 bg-white pb-2">
            <label className="flex flex-col min-w-0 gap-0">
              <span className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 sm:text-xs">
                Subject
              </span>
              <input
                type="text"
                className="min-w-0 flex-1 py-1 text-sm font-medium text-slate-800 outline-none transition border-b border-slate-50/10  hover:border-slate-400 focus:border-[#365561] disabled:text-slate-400"
                value={subject || ""}
                onChange={(e) => setSubject(e.target.value)}
                disabled={isConnection}
                placeholder="Subject"
              />
            </label>
          </div>

          <div className="mb-1.5 flex items-center justify-between gap-2">
            <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
              {isConnection ? "Connection note" : "Email body"}
            </span>
            {isConnection && (
              <span className={[
                "text-xs font-semibold",
                body?.length > 300 ? "text-rose-500" : "text-slate-400",
              ].join(" ")}
              >
                {(body || "").length}/300
              </span>
            )}
          </div>

          {isConnection ? (
            <textarea
              className="min-h-[320px] w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm leading-6 text-slate-800 outline-none transition focus:border-[#365561]/30 focus:shadow-[0_10px_24px_rgba(54,85,97,0.08)]"
              value={body || ""}
              onChange={(e) => setBody(e.target.value)}
              rows={12}
            />
          ) : (
            <TemplateQuillEditor value={body || ""} onChange={setBody} minHeight={320} />
          )}
        </div>

        <div className="shrink-0 rounded-b-3xl border-t border-slate-200 bg-white/80 px-4 py-3 backdrop-blur-xl">
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
            {selectedIsMine ? (
              selectedIsDefault ? (
                <button
                  type="button"
                  onClick={() => onSaveChanges?.()}
                  disabled={!selectedTemplate || !hasTemplateChanges}
                  className="rounded-2xl bg-[#365561] px-4 py-2 text-xs font-semibold text-white sm:text-sm shadow-[0_10px_24px_rgba(54,85,97,0.22)] transition hover:bg-[#2c4650] disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none"
                >
                  Save changes
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={onSetDefault}
                    disabled={!selectedTemplate}
                    className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-600 sm:text-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none"
                  >
                    Set as default
                  </button>
                  <button
                    type="button"
                    onClick={() => onSaveChanges?.()}
                    disabled={!selectedTemplate || !hasTemplateChanges}
                    className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-600 sm:text-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none"
                  >
                    Save changes
                  </button>
                  <button
                    type="button"
                    onClick={() => onSaveAndSetDefault?.()}
                    disabled={!selectedTemplate || !hasTemplateChanges}
                    className="rounded-2xl bg-[#365561] px-4 py-2 text-xs font-semibold text-white sm:text-sm shadow-[0_10px_24px_rgba(54,85,97,0.22)] transition hover:bg-[#2c4650] disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none"
                  >
                    Save & set as default
                  </button>
                </>
              )
            ) : selectedIsSystemDefault ? (
              <button
                type="button"
                onClick={() => onCopyAndSetDefault?.()}
                disabled={!selectedTemplate || creatingNewExisting}
                className="rounded-2xl bg-[#365561] px-4 py-2 text-xs font-semibold text-white sm:text-sm shadow-[0_10px_24px_rgba(54,85,97,0.22)] transition hover:bg-[#2c4650] disabled:cursor-wait disabled:opacity-60 focus:outline-none"
              >
                {creatingNewExisting ? (
                  <span className="inline-flex items-center gap-2">
                    <i className="fa-solid fa-spinner fa-spin" /> Copying...
                  </span>
                ) : (
                  "Copy to My Templates"
                )}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => onReviewTemplate?.()}
                  disabled={!selectedTemplate || creatingNewExisting || !hasTemplateChanges}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-600 sm:text-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none"
                >
                  Save changes
                </button>
                <button
                  type="button"
                  onClick={() => onCopyToMine?.()}
                  disabled={!selectedTemplate || creatingNewExisting}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-600 sm:text-sm transition hover:bg-slate-50 disabled:cursor-wait disabled:opacity-60 focus:outline-none"
                >
                  {creatingNewExisting ? (
                    <span className="inline-flex items-center gap-2">
                      <i className="fa-solid fa-spinner fa-spin" /> Copying...
                    </span>
                  ) : (
                    "Copy to My Templates"
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => onCopyAndSetDefault?.()}
                  disabled={!selectedTemplate || creatingNewExisting}
                  className="rounded-2xl bg-[#365561] px-4 py-2 text-xs font-semibold text-white sm:text-sm shadow-[0_10px_24px_rgba(54,85,97,0.22)] transition hover:bg-[#2c4650] disabled:cursor-wait disabled:opacity-60 focus:outline-none"
                >
                  Copy & set as default
                </button>
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function TemplateQuillEditor({ value, onChange, minHeight = 280 }) {
  return (
    <div className="template-manager-editor overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <ReactQuill
        theme="snow"
        value={value}
        onChange={onChange}
        modules={{
          toolbar: [
            [{ font: TemplateFont.whitelist }],
            [{ header: [1, 2, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link"],
          ],
        }}
        formats={[
          "font",
          "header",
          "bold",
          "italic",
          "underline",
          "strike",
          "list",
          "bullet",
          "link",
        ]}
      />
      <style>
        {`
          .template-manager-editor .ql-editor {
            font-family: Arial, sans-serif;
            font-size: 14px;
            line-height: 1.45;
            min-height: ${minHeight}px;
            padding: 14px 16px !important;
          }

          .template-manager-editor .ql-container {
            height: auto !important;
            min-height: ${minHeight}px;
            border: 0 !important;
            padding-bottom: 10px;
          }

          .template-manager-editor .ql-container .ql-snow .ql-hidden {
            display: none !important;
          }

          .template-manager-editor .ql-toolbar.ql-snow {
            position: inherit;
            top: 0;
            right: 24px;
            padding: 6px 8px;
            display: flex;
            flex-wrap: wrap;
            gap: 6px;
            border: 0 !important;
            border-bottom: 1px solid rgb(226 232 240) !important;
            background: rgb(248 250 252);
          }

          .ql-font-arial { font-family: Arial, sans-serif; }
          .ql-font-georgia { font-family: Georgia, serif; }
          .ql-font-times-new-roman { font-family: "Times New Roman", serif; }
          .ql-font-courier-new { font-family: "Courier New", monospace; }
          .ql-font-verdana { font-family: Verdana, sans-serif; }
          .ql-font-tahoma { font-family: Tahoma, sans-serif; }
          .ql-font-lucida { font-family: "Lucida Sans Unicode", sans-serif; }
          .ql-font-roboto { font-family: "Roboto", sans-serif; }
          .ql-font-lato { font-family: "Lato", sans-serif; }

          .ql-snow .ql-picker.ql-font .ql-picker-label::before,
          .ql-snow .ql-picker.ql-font .ql-picker-item::before {
            content: attr(data-value);
            font-family: inherit;
          }

          .ql-picker-item[data-value="arial"]::before,
          .ql-picker-label[data-value="arial"]::before {
            font-family: Arial, sans-serif;
            content: "Arial";
          }

          .ql-picker-item[data-value="georgia"]::before,
          .ql-picker-label[data-value="georgia"]::before {
            font-family: Georgia, serif;
            content: "Georgia";
          }

          .ql-picker-item[data-value="times-new-roman"]::before,
          .ql-picker-label[data-value="times-new-roman"]::before {
            font-family: "Times New Roman", serif;
            content: "Times New Roman";
          }

          .ql-picker-item[data-value="courier-new"]::before,
          .ql-picker-label[data-value="courier-new"]::before {
            font-family: "Courier New", monospace;
            content: "Courier New";
          }

          .ql-picker-item[data-value="verdana"]::before,
          .ql-picker-label[data-value="verdana"]::before {
            font-family: Verdana, sans-serif;
            content: "Verdana";
          }

          .ql-picker-item[data-value="tahoma"]::before,
          .ql-picker-label[data-value="tahoma"]::before {
            font-family: Tahoma, sans-serif;
            content: "Tahoma";
          }

          .ql-picker-item[data-value="lucida"]::before,
          .ql-picker-label[data-value="lucida"]::before {
            font-family: "Lucida Sans Unicode", sans-serif;
            content: "Lucida";
          }

          .ql-editor ul {
            padding-left: 1.25em;
            margin: 0.5em 0;
          }
        `}
      </style>
    </div>
  );
}

export function CreateNew({ project_id, setCreateNew, initialTemplateType = "Email", setTemplate = () => {}, onBack = null, onCreated = null }) {
  const { API_URL } = useContext(UrlContext);
  const [outreachTemplates, setOutreachTemplates] = useState([]);
  const [inviteTemplates, setInviteTemplates] = useState([]);
  const [followUpTemplates, setFollowUpTemplates] = useState([]);
  const [connTemplates, setConnTemplates] = useState([]);
  const [templateType, setTemplateType] = useState(initialTemplateType || "Email");
  const [language, setLanguage] = useState("English");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  useEffect(() => {
    if (initialTemplateType && initialTemplateType !== templateType) {
      setTemplateType(initialTemplateType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTemplateType]);

  const Block = Quill.import("blots/block");
  Block.tagName = "div";
  Quill.register(Block, true);

  const Font = Quill.import("formats/font");
  Font.whitelist = [
    "arial",
    "georgia",
    "times-new-roman",
    "courier-new",
    "verdana",
    "tahoma",
    "lucida",
  ];
  Quill.register(Font, true);

  const getToken = () => {
    const tokenObj = localStorage.getItem("eph-token");
    return JSON.parse(tokenObj)?.value || "";
  };

  // Fetch default templates on mount
  useEffect(() => {
    const fetchDefaultTemplates = async () => {
      const projectDetails = await getProjectDetails();
      if (projectDetails) {
        const outreach = generateOutreachTemplates(
          projectDetails,
          projectDetails.cons_exp
        );
        const invite = generateInviteTemplates(
          projectDetails.projectName,
          projectDetails.description
        );
        const followUp = generateFollowUpTemplates(
          projectDetails.projectName,
          projectDetails.description,
          projectDetails.cons_exp
        );
        const connection = generateInMailTemplates(projectDetails.projectName);
        setOutreachTemplates(outreach);
        setInviteTemplates(invite);
        setFollowUpTemplates(followUp);
        setConnTemplates(connection);
        setTemplateType(initialTemplateType || "Email"); // Keep current template type when opening create mode
      }
    };
    fetchDefaultTemplates();
  }, []);

  // API to call project details
  const getProjectDetails = async () => {
    const requestBody = {
      projectID: project_id,
    };
    try {
      let modifiedUrl = "get-project-details-for-template";
      const response = await fetch(`${API_URL}${modifiedUrl}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      const responseData = await response.json();
      if (responseData.status === "success") {
        return responseData.projectData;
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to fetch project details.",
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch project details.",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    }
  };

  // Set template based on type and language
  useEffect(() => {
    let templateSet = [];

    // Pick the correct set based on templateType
    if (templateType === "Email") {
      templateSet = outreachTemplates;
    } else if (templateType === "Invite") {
      templateSet = inviteTemplates;
    } else if (templateType === "FollowUp") {
      templateSet = followUpTemplates;
    } else if (templateType === "Connection") {
      templateSet = connTemplates;
    }

    // Find template for current language
    const currentTemplate =
      templateSet.find(
        (tpl) =>
          tpl.label?.toLowerCase().trim() === language?.toLowerCase().trim()
      ) || templateSet[0];

    if (currentTemplate) {
      setSubject(currentTemplate.subject || "");
      setBody(currentTemplate.value || "");
    }
  }, [templateType, language, outreachTemplates, inviteTemplates, followUpTemplates, connTemplates]);

  // API to create new template
  const createNewTemplate = async () => {
    const token = getToken();
    const requestBody = {
      projectID: project_id,
      token: token,
      templateType: templateType,
      subject: subject,
      body: body,
      language: language === "English" ? 'English' : 'German',
    };
    try {
      const response = await fetch(`${API_URL}create-new-template`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      const responseData = await response.json();
      if (responseData.status === "success") {
        Swal.fire({
          icon: "success",
          title: "Template Created",
          text: "Your template has been created successfully.",
          timer: 1000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
        setTemplate(templateType);
        if (onCreated) {
          onCreated(templateType);
        } else {
          setCreateNew(false);
        }
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to fetch project details.",
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch project details.",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    }
  };

  const generateOutreachTemplates = (project, cons_exp) => {
    const buildDetails = (lang) => {
      const startDate = new Date(project.projStartDate);
      const lines =
        lang === "en"
          ? [
              project.description,
              "Details:",
              `Project Start Date: ${startDate.toLocaleDateString("en-GB", {
                weekday: "short",
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}`,
              `Project Duration: ${project.projDuration}`,
              `Work mode: ${project.empType}`,
              `Industry: ${project.industry}`,
            ]
          : [
              project.description,
              "Einzelheiten:",
              `Projektstartdatum: ${startDate.toLocaleDateString("de-DE", {
                weekday: "short",
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}`,
              `Projektdauer: ${project.projDuration}`,
              `Arbeitsmodus: ${project.empType}`,
              `Industrie: ${project.industry}`,
            ];

      if (cons_exp === "exp") {
        lines.push(
          lang === "en"
            ? "This would be an attractively paid 1-hour discussion by phone."
            : "Dabei handelt es sich um ein attraktiv bezahltes 1-stündiges Telefongespräch."
        );
      }
      return lines.join("<br />");
    };

    return [
      {
        lang: "en",
        label: "English",
        name: "Outreach – EN default",
        subject: `expertpowerhouse | ${project.projectName}`,
        value: `
          <div>Dear first_name,</div>
          <div><br></div>
          <div>As we have not yet been in contact please allow me to introduce myself. My name is user_name from expertpowerhouse GmbH - an agency matching experts and projects globally.</div>
          <div><br></div>
          ${buildDetails("en")}
          <div><br></div>
          <div>Would that be of interest to you at all?</div>
          <div><br></div>
          <div>I would in any case be very happy to receive a brief reply.</div>
          <div><br></div>
          <p>Best regards</p>
          <p>first_user</p>
        `,
      },
      {
        lang: "de",
        label: "German - formal",
        name: "Outreach – DE formal",
        subject: `expertpowerhouse | ${project.projectName}`,
        value: `
          <div>Sehr geehrte/r Herr/Frau last_name,</div>
          <div><br></div>
          <div>da wir noch keinen Kontakt hatten, gestatten Sie mir bitte, mich vorzustellen. Mein Name ist user_name von der expertpowerhouse GmbH - einer Agentur, die weltweit Experten und Projekte vermittelt.</div>
          <div><br></div>
          ${buildDetails("de")}
          <div><br></div>
          <div>Wäre das für Sie überhaupt interessant?</div>
          <div><br></div>
          <div>Über eine kurze Antwort würde ich mich jedenfalls sehr freuen.</div>
          <div><br></div>
          <div>Mit freundlichen Grüßen</div>
          <div>user_name</div>
        `,
      },
      {
        lang: "de",
        label: "German - informal",
        name: "Outreach – DE informal",
        subject: `expertpowerhouse | ${project.projectName}`,
        value: `
          <div>Hallo first_name,</div>
          <div><br></div>
          <div>da wir noch keinen Kontakt hatten, gestatte mir bitte, mich vorzustellen. Mein Name ist user_name von der expertpowerhouse GmbH - einer Agentur, die weltweit Experten und Projekte vermittelt.</div>
          <div><br></div>
          ${buildDetails("de")}
          <div><br></div>
          <div>Wäre das für dich überhaupt interessant?</div>
          <div><br></div>
          <div>Über eine kurze Antwort würde ich mich jedenfalls sehr freuen.</div>
          <div><br></div>
          <div>Viele Grüße</div>
          <div>first_user</div>
        `,
      },
    ];
  };

  const generateInviteTemplates = (projectName, projectDescription) => [
    {
      lang: "en",
      label: "English",
      name: "Project Invitation - EN default",
      subject: `expertpowerhouse | Invitation to Project Opportunity: ${projectName}`,
      value: `
        <div>Dear first_name,</div>
        <div><br></div>
        <div>We believe you could be a great fit for the following project opportunity:</div>
        <div><br></div>
        <blockquote style="padding: 10px 16px; background-color: #f9f9f9; border-left: 4px solid #365561; font-style: italic;">
          <strong>"${projectName}"</strong><br>
          ${projectDescription}
        </blockquote>
        <div><br></div>
        <div>If you are available and interested, please click the button below to access your consultant profile. Once there, navigate to the "Invitations" tab to express your interest.</div>
        {{BUTTON_DO_NOT_REMOVE}}
        <div><br></div>
        <div>Best regards,</div>
        <div>first_user</div>
      `,
    },
    {
      lang: "de",
      label: "German - formal",
      name: "Project Invitation - DE formal",
      subject: `expertpowerhouse | Einladung zur Projektchance: ${projectName}`,
      value: `
        <div>Sehr geehrte/r Herr/Frau last_name,</div>
        <div><br></div>
        <div>ich hoffe Ihnen geht es gut! Ich glaube, dass Sie sehr gut zu folgendem Projekt passen könnten:</div>
        <div><br></div>
        <blockquote style="padding: 10px 16px; background-color: #f9f9f9; border-left: 4px solid #365561; font-style: italic;">
          <strong>"${projectName}"</strong><br>
          ${projectDescription}
        </blockquote>
        <div><br></div>
        <div>Wenn Sie verfügbar und interessiert sind, können Sie direkt über den Button unten Ihr Beraterprofil aufrufen, um dort im Bereich "Invitations" Ihr Interesse zu bestätigen.</div>
        {{BUTTON_DO_NOT_REMOVE}}
        <div><br></div>
        <div>Mit freundlichen Grüßen</div>
        <div>user_name</div>
      `,
    },
    {
      lang: "de",
      label: "German - informal",
      name: "Project Invitation - DE informal",
      subject: `expertpowerhouse | Einladung zur Projektchance: ${projectName}`,
      value: `
        <div>Hallo first_name,</div>
        <div><br></div>
        <div>ich hoffe dir geht es gut! Ich glaube, dass du sehr gut zu folgendem Projekt passen könntest:</div>
        <div><br></div>
        <blockquote style="padding: 10px 16px; background-color: #f9f9f9; border-left: 4px solid #365561; font-style: italic;">
          <strong>"${projectName}"</strong><br>
          ${projectDescription}
        </blockquote>
        <div><br></div>
        <div>Wenn du verfügbar und interessiert bist kannst du direkt über den Button unten dein Beraterprofil aufrufen, um dort im Bereich "Invitations" dein interesse zu bestätigen.</div>
        {{BUTTON_DO_NOT_REMOVE}}
        <div><br></div>
        <div>Viele Grüße,</div>
        <div>first_user</div>
      `,
    },
  ];

  const generateFollowUpTemplates = (projectName, projectDescription, cons_exp) => [
    {
      lang: "en",
      label: "English",
      name: "Follow-Up - EN",
      subject: `expertpowerhouse | Follow-up regarding our previous conversation: ${projectName}`,
      value: cons_exp === "exp" ? 
      `
        <div>Hi first_name,</div>
        <div><br></div>
        <div>I just wanted to quickly follow up and see if you had a chance to review my previous message?</div>
        <div><br></div>
        <div>Kindly let me know if you have any questions or concerns! Our client operates on a tight timeline - if you'd have a moment to respond today that would be highly appreciated! Also happy to give you a call whenever convenient.</div>
        <div><br></div>
        <div>Kind regards,</div>
        <div>first_user</div>
      ` :
      `
        <div>Dear first_name,</div>
        <div><br></div>
        <div>Thank you very much for your interest in this engagement. As previously mentioned, the project concerns the following:</div>
        <div><br></div>
        <blockquote style="padding: 10px 16px; background-color: #f9f9f9; border-left: 4px solid #365561; font-style: italic;">
          <strong>"${projectName}"</strong><br>
          ${projectDescription}
        </blockquote>
        <div><br></div>
        <div>If the project remains of interest to you, it would be greatly appreciated if you could share a few sentences outlining your relevant experience. This will help us position you in the best possible way with the client. In addition, could you please let us know your expected daily rate for this project?</div>
        <div><br></div>
        <div>If you have any questions or require further information, I would be happy to assist you.</div>
        <div>We would also be glad to arrange a short call at your convenience.</div>
        <div><br></div>
        <div>Kind regards,</div>
        <div>first_user</div>
      `,
    },
    {
      lang: "de",
      label: "German - formal",
      name: "Follow-Up - DE formal",
      subject: `expertpowerhouse | Nachfrage zu unserer vorherigen Nachricht: ${projectName}`,
      value: cons_exp === "exp" ? 
      `
        <div>Guten Tag Herr/Frau last_name,</div> 
        <div><br></div> 
        <div>ich wollte nur kurz nachfragen, ob Sie die Möglichkeit hatten, meine vorherige Nachricht zu lesen?</div> 
        <div><br></div> 
        <div>Bitte lassen Sie mich wissen, wenn Sie noch Fragen oder Bedenken haben! Unser Kunde hat hier etwas Zeitdruck. Wenn Sie also weiterhin Interesse haben, wäre es klasse, wenn Sie mir heute antworten könnten. Für eine Rückmeldung wäre ich Ihnen jedenfalls sehr dankbar! Ich rufe Sie auch gerne an, wenn es Ihnen passt.</div> 
        <div><br></div> 
        <div>Mit freundlichen Grüßen</div> 
        <div>user_name</div>
      ` :
      `
        <div>Guten Tag Herr/Frau last_name,</div>
        <div><br></div>
        <div>vielen Dank für Ihr Interesse an dem Engagement. Wie bereits geschrieben geht es um folgendes:</div>
        <div><br></div>
        <blockquote style="padding: 10px 16px; background-color: #f9f9f9; border-left: 4px solid #365561; font-style: italic;">
          <strong>"${projectName}"</strong><br>
          ${projectDescription}
        </blockquote>
        <div><br></div>
        <div>Falls das Projekt weiterhin für Sie interessant ist, wäre es sehr hilfreich, wenn Sie mir ein paar Sätze mit Ihrer relevanten Erfahrung mitteilen könnten. Das hilft uns, Sie bestmöglich beim Kunden zu positionieren. Außerdem: was ist Ihre Tagessatzerwartung für dieses Projekt?</div>
        <div><br></div>
        <div>Wenn Sie noch Fragen haben oder weitere Informationen benötigen, stehe ich Ihnen selbstverständlich gerne zur Verfügung.</div>
        <div>Gerne können wir auch kurzfristig telefonieren, falls das für Sie einfacher ist.</div>
        <div><br></div>
        <div>Mit freundlichen Grüßen</div>
        <div>user_name</div>
      `,
    },
    {
      lang: "de",
      label: "German - informal",
      name: "Follow-Up - DE informal",
      subject: `expertpowerhouse | Nachfrage zu unserer vorherigen Nachricht: ${projectName}`,
      value: cons_exp === "exp" ? 
      `
        <div>Guten Tag first_name,</div> 
        <div><br></div> 
        <div>ich wollte nur kurz nachfragen, ob Du die Möglichkeit hattest, meine vorherige Nachricht zu lesen?</div> 
        <div><br></div> 
        <div>Bitte lass mich wissen, wenn Du noch Fragen oder Bedenken hast! Unser Kunde hat hier etwas Zeitdruck. Wenn Du also weiterhin Interesse hast, wäre es klasse, wenn Du mir heute antworten könntest. Für eine Rückmeldung wäre ich Dir jedenfalls sehr dankbar! Ich rufe Dich auch gerne an, wenn es Dir passt.</div> 
        <div><br></div> 
        <div>Mit freundlichen Grüßen</div> 
        <div>first_user</div>
      ` :
      `
        <div>Hallo first_name,</div>
        <div><br></div>
        <div>vielen Dank für dein Interesse an dem Projekt. Wie bereits geschrieben geht es um folgendes:</div>
        <div><br></div>
        <blockquote style="padding: 10px 16px; background-color: #f9f9f9; border-left: 4px solid #365561; font-style: italic;">
          <strong>"${projectName}"</strong><br>
          ${projectDescription}
        </blockquote>
        <div><br></div>
        <div>Falls das Projekt weiterhin für dich interessant ist, wäre es sehr hilfreich, wenn du mir ein paar Sätze mit deiner relevanten Erfahrung mitteilen könntest. Das hilft uns, dich bestmöglich beim Kunden zu positionieren. Außerdem: was ist deine Tagessatzerwartung für dieses Projekt?</div>
        <div><br></div>
        <div>Wenn du noch Fragen hast oder weitere Informationen benötigst, stehe ich dir selbstverständlich gerne zur Verfügung.</div>
        <div>Gerne können wir auch kurzfristig telefonieren, falls das für dich einfacher ist.</div>
        <div><br></div>
        <div>Viele Grüße</div>
        <div>first_user</div>
      `,
    },
  ];

  const generateInMailTemplates = (projectName) => [
    {
      lang: "en",
      label: "English",
      name: "LinkedIn InMail Outreach - EN",
      subject: "", // LinkedIn InMails don't use a separate subject line
      value: `
  Dear first_name,

I am user_name from expertpowerhouse, a firm that matches global projects and consultants. Currently, I have one project related to ${projectName} and would like to discuss it with you. Shall we connect to discuss further?

Best regards,
user_name
    `.trim(),
    },
    {
      lang: "de",
      label: "German - formal",
      name: "LinkedIn InMail Outreach - DE formal",
      subject: "",
      value: `
  Hallo Herr/Frau last_name,

ich bin user_name von expertpowerhouse, einem Unternehmen, das globale Projekte und Berater zusammenbringt. Derzeit habe ich ein Projekt im Zusammenhang mit ${projectName} und möchte es mit Ihnen besprechen. Sollen wir uns vernetzen, um weitere Gespräche zu führen?

Beste Grüße,
user_name
    `.trim(),
    },
    {
      lang: "de",
      label: "German - informal",
      name: "LinkedIn InMail Outreach - DE informal",
      subject: "",
      value: `
  Liebe/r first_name,

ich bin user_name von expertpowerhouse, einem Unternehmen, das globale Projekte und Berater zusammenbringt. Derzeit habe ich ein Projekt im Zusammenhang mit ${projectName} und möchte es mit dir besprechen. Sollen wir uns vernetzen, um weitere Gespräche zu führen?

Beste Grüße,
user_name
    `.trim(),
    },
  ];

  const selectedTypeLabel = getTemplateTypeLabel(templateType);
  const isConnectionTemplate = templateType === "Connection";

  return (
    <div className="flex h-full min-h-0 flex-col rounded-3xl border border-slate-200/80 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.06)]">
      <div className="shrink-0 rounded-t-3xl border-b border-slate-200 bg-white/85 px-4 py-3 backdrop-blur-xl">
        <div className="flex flex-row gap-3  items-end justify-between">
            <div className="min-w-0">
              <h3 className="truncate text-base font-semibold text-slate-900">
                New Template
              </h3>
              <p className="mt-1 text-xs text-slate-500">
                Create new template
              </p>
            </div>
            <LanguageCategoryToggle value={language} onChange={setLanguage} />
          </div>
      </div>

      <div className={`${scrollDesign.scrollbarOverlay} min-h-0 flex-1 overflow-y-auto px-4 py-4`}>
        <div className="shrink-0 bg-white pb-2">
          <label className="flex flex-col min-w-0 gap-0">
            <span className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 sm:text-xs">
              Subject
            </span>
            <input
              type="text"
              className="min-w-0 flex-1 py-1 text-sm font-medium text-slate-800 outline-none transition border-b border-slate-50/10  hover:border-slate-400 focus:border-[#365561] disabled:text-slate-400"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={isConnectionTemplate ? "No subject required" : "Subject"}
              disabled={isConnectionTemplate}
            />
          </label>
        </div>

        <div className="mb-1.5 flex items-center justify-between gap-2">
          <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
            {isConnectionTemplate ? "Connection note" : "Email body"}
          </span>
          {isConnectionTemplate && (
            <span className={[
              "text-xs font-semibold",
              body.length > 300 ? "text-rose-500" : "text-slate-400",
            ].join(" ")}
            >
              {body.length}/300
            </span>
          )}
        </div>

        {isConnectionTemplate ? (
          <textarea
            className="min-h-[320px] w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm leading-6 text-slate-800 outline-none transition focus:border-[#365561]/30 focus:shadow-[0_10px_24px_rgba(54,85,97,0.08)]"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={12}
          />
        ) : (
          <TemplateQuillEditor value={body} onChange={setBody} minHeight={320} />
        )}
      </div>

      <div className="shrink-0 rounded-b-3xl border-t border-slate-200 bg-white/80 px-4 py-3 backdrop-blur-xl">
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
          <IconExpandButton
            icon="fa-arrow-left"
            label={`Back to ${selectedTypeLabel} templates`}
            onClick={() => (onBack ? onBack(templateType) : setCreateNew(false))}
            className="border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
            iconClassName="bg-white text-slate-600"
            labelClassName="text-slate-600"
          />

          <button
            type="button"
            onClick={() => createNewTemplate()}
            className="rounded-2xl bg-[#365561] px-4 py-2 text-xs font-semibold text-white sm:text-sm shadow-[0_10px_24px_rgba(54,85,97,0.22)] transition hover:bg-[#2c4650] focus:outline-none"
          >
            Create template
          </button>
        </div>
      </div>
    </div>
  );
}


export function OutreachEmail({ project_id }) {
  const { API_URL } = useContext(UrlContext);
  const { userData } = useContext(UserContext);
  const [allTemplates, setAllTemplates] = useState([]);
  const [templateList, setTemplateList] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [language, setLanguage] = useState("English");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [isIncludeAll, setIsIncludeAll] = useState(true);
  const [creatingNewExisting, setCreatingNewExisting] = useState(false);
  const [outreachLoading, setOutreachLoading] = useState(false);

  const Block = Quill.import("blots/block");
  Block.tagName = "div";
  Quill.register(Block, true);

  const Font = Quill.import("formats/font");
  Font.whitelist = [
    "arial",
    "georgia",
    "times-new-roman",
    "courier-new",
    "verdana",
    "tahoma",
    "lucida",
  ];
  Quill.register(Font, true);

  const getToken = () => {
    const tokenObj = localStorage.getItem("eph-token");
    return JSON.parse(tokenObj)?.value || "";
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (!isIncludeAll) {
      const filteredTemplates = allTemplates.filter((t) => {
        return t.created_by === userData.username;
      });
      updateTemplateList(filteredTemplates);
    } else {
      updateTemplateList(allTemplates);
    }
  }, [isIncludeAll]);

  const fetchTemplates = async () => {
    setOutreachLoading(true);
    try {
      const response = await fetch(
        `${API_URL}get-outreach-templates/${project_id}`,
        {
          headers: {
            "x-access-token": getToken(),
          },
        }
      );
      const data = await response.json();

      if (data.status !== "success") {
        Swal.fire({
          icon: "error",
          title: "Failed to Fetch Templates",
          text: "Backend error while fetching outreach templates.",
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }

      const templatesFromDB = data.templates || [];
      const projectDetails = await fetchProjectDetailsForTemplate(API_URL, project_id);
      const fallbackTemplates = buildFallbackTemplateItems("Email", projectDetails);
      const templatesWithFallbacks = [...templatesFromDB, ...fallbackTemplates];

      setAllTemplates(templatesWithFallbacks);
      updateTemplateList(templatesWithFallbacks);
      return templatesWithFallbacks;
    } catch (err) {
      console.error("Failed to fetch outreach templates:", err);
      return [];
    } finally {
      setOutreachLoading(false);
    }
  };

  const updateTemplateList = (templates) => {
    if (templates.length > 0) {
      const defaultTemplate =
        templates.find((t) => t.isDefault) || templates[0];
      setSelectedTemplate(defaultTemplate);
      setLanguage(defaultTemplate.language);
      setSubject(defaultTemplate.subject);
      setBody(defaultTemplate.body);
    }
    setTemplateList(templates);
  };

  const handleTemplateChange = (e) => {
    const template = templateList.find(
      (t) => String(t.id) === String(e.target.value)
    );
    if (template) {
      setSelectedTemplate(template);
      setLanguage(template.language);
      setSubject(template.subject);
      setBody(template.body);
    }
  };

  const handleUpdate = async ({ showSuccess = true } = {}) => {
    if (!selectedTemplate) return alert("No template selected to update.");

    try {
      const response = await fetch(
        `${API_URL}update-outreach-template/${selectedTemplate.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "x-access-token": getToken(),
          },
          body: JSON.stringify({
            language,
            subject,
            body,
          }),
        }
      );

      if (!response.ok) {
        Swal.fire({
          icon: "error",
          title: "Failed to Update",
          text: "Backend error while updating template.",
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
      await fetchTemplates();
      if (showSuccess) {
        Swal.fire({
          icon: "success",
          title: "Template Updated",
          text: "Your template has been updated successfully.",
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Failed to Update",
        text: "Error while updating template.",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    }
  };

  const markAsDefault = async (templateID = selectedTemplate?.id) => {
    if (!templateID) return;

    try {
      const response = await fetch(`${API_URL}mark-as-default`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": getToken(),
        },
        body: JSON.stringify({
          templateType: "outreach",
          projectID: project_id,
          templateID: templateID,
          isDefault: true,
        }),
      });

      if (!response.ok) {
        Swal.fire({
          icon: "error",
          title: "Failed to Add Default",
          text: "Backend error while adding default template.",
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
      // await fetchTemplates();
    } catch (err) {
      console.error("Failed to add as default template:", err);
    }
  };

  const createNewFromExisting = async (makeDefaultAfterCopy = false) => {
    setCreatingNewExisting(true);
    const token = getToken();
    const requestBody = {
      projectID: project_id,
      token: token,
      templateType: "Email",
      subject: subject,
      body: body,
      language: language,
    };
    try {
      const response = await fetch(
        `${API_URL}create-new-from-existing-template`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      const responseData = await response.json();
      if (responseData.status === "success") {
        const copiedTemplateID =
          responseData.templateID ||
          responseData.templateId ||
          responseData.newTemplateID ||
          responseData.id ||
          responseData.template?.id;

        if (makeDefaultAfterCopy && copiedTemplateID) {
          await markAsDefault(copiedTemplateID);
        }

        const refreshedTemplates = await fetchTemplates();

        if (makeDefaultAfterCopy && !copiedTemplateID) {
          const copiedTemplate = (refreshedTemplates || []).find((template) => {
            const sameOwner = template.created_by === userData.username;
            const sameSubject = String(template.subject || "") === String(subject || "");
            const sameBody = String(template.body || "") === String(body || "");
            const sameLanguage = String(template.language || "") === String(language || "");
            return sameOwner && sameSubject && sameBody && sameLanguage;
          });

          if (copiedTemplate?.id) {
            await markAsDefault(copiedTemplate.id);
            await fetchTemplates();
          }
        }
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to fetch project details.",
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch project details.",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    } finally {
      setCreatingNewExisting(false);
    }
  };

  const handleTemplateSelect = (templateID) => {
    const template = templateList.find((t) => String(t.id) === String(templateID));
    if (template) {
      setSelectedTemplate(template);
      setLanguage(template.language);
      setSubject(template.subject || "");
      setBody(template.body || "");
    }
  };

  const handleSetDefault = async () => {
    if (!selectedTemplate) return;
    await markAsDefault();
    await fetchTemplates();
    Swal.fire({
      icon: "success",
      title: "Default Template Updated",
      text: "This template is now your default for this project.",
      timer: 1800,
      timerProgressBar: true,
      showConfirmButton: false,
    });
  };

  const handleSaveAndSetDefault = async () => {
    if (!selectedTemplate) return;
    await handleUpdate({ showSuccess: false });
    await markAsDefault(selectedTemplate.id);
    await fetchTemplates();
    Swal.fire({
      icon: "success",
      title: "Template Saved and Set as Default",
      text: "Your changes were saved and this template is now the default.",
      timer: 2000,
      timerProgressBar: true,
      showConfirmButton: false,
    });
  };

  return (
    <TemplateManagerWorkspace
      title="First outreach"
      emptyMessage={'Please click on "Create New Template" to create an Outreach template.'}
      loading={outreachLoading}
      allTemplates={allTemplates}
      templateList={templateList}
      selectedTemplate={selectedTemplate}
      onSelectTemplate={handleTemplateSelect}
      isIncludeAll={isIncludeAll}
      setIsIncludeAll={setIsIncludeAll}
      userName={userData.username}
      language={language}
      subject={subject}
      body={body}
      setSubject={setSubject}
      setBody={setBody}
      isConnection={false}
      creatingNewExisting={creatingNewExisting}
      onCopyToMine={() => createNewFromExisting(false)}
      onCopyAndSetDefault={() => createNewFromExisting(true)}
      onSaveChanges={handleUpdate}
      onSaveAndSetDefault={handleSaveAndSetDefault}
      onSetDefault={handleSetDefault}
      onReviewTemplate={handleUpdate}
    />
  );
}

export function FollowupEmail({ project_id }) {
  const { API_URL } = useContext(UrlContext);
  const { userData } = useContext(UserContext);
  const [allTemplates, setAllTemplates] = useState([]);
  const [templateList, setTemplateList] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [language, setLanguage] = useState("English");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [isIncludeAll, setIsIncludeAll] = useState(true);
  const [creatingNewExisting, setCreatingNewExisting] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);

  const getToken = () => {
    const tokenObj = localStorage.getItem("eph-token");
    return JSON.parse(tokenObj)?.value || "";
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (!isIncludeAll) {
      const filteredTemplates = allTemplates.filter((t) => {
        return t.created_by === userData.username;
      });
      updateTemplateList(filteredTemplates);
    } else {
      updateTemplateList(allTemplates);
    }
  }, [isIncludeAll]);

  const fetchTemplates = async () => {
    setFollowLoading(true);
    try {
      const response = await fetch(
        `${API_URL}get-followup-templates/${project_id}`,
        {
          headers: {
            "x-access-token": getToken(),
          },
        }
      );
      const data = await response.json();

      if (data.status !== "success") {
        Swal.fire({
          icon: "error",
          title: "Failed to Fetch Templates",
          text: "Backend error while fetching outreach templates.",
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }

      const templatesFromDB = data.templates || [];
      const projectDetails = await fetchProjectDetailsForTemplate(API_URL, project_id);
      const fallbackTemplates = buildFallbackTemplateItems("FollowUp", projectDetails);
      const templatesWithFallbacks = [...templatesFromDB, ...fallbackTemplates];

      setAllTemplates(templatesWithFallbacks);
      updateTemplateList(templatesWithFallbacks);
      return templatesWithFallbacks;
    } catch (err) {
      console.error("Failed to fetch follow-up templates:", err);
      return [];
    } finally {
      setFollowLoading(false);
    }
  };

  const updateTemplateList = (templates) => {
    if (templates.length > 0) {
      const defaultTemplate =
        templates.find((t) => t.isDefault) || templates[0];
      setSelectedTemplate(defaultTemplate);
      setLanguage(defaultTemplate.language);
      setSubject(defaultTemplate.subject);
      setBody(defaultTemplate.body);
    }
    setTemplateList(templates);
  };

  const handleTemplateChange = (e) => {
    const tpl = templateList.find((t) => String(t.id) === String(e.target.value));
    if (tpl) {
      setSelectedTemplate(tpl);
      setLanguage(tpl.language);
      setSubject(tpl.subject);
      setBody(tpl.body);
    }
  };

  const handleUpdate = async ({ showSuccess = true } = {}) => {
    if (!selectedTemplate) return alert("No template selected to update.");

    try {
      const response = await fetch(
        `${API_URL}update-followup-template/${selectedTemplate.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "x-access-token": getToken(),
          },
          body: JSON.stringify({
            language,
            subject,
            body,
          }),
        }
      );

      if (!response.ok) {
        Swal.fire({
          icon: "error",
          title: "Failed to Update",
          text: "Backend error while updating template.",
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
      await fetchTemplates();
      if (showSuccess) {
        Swal.fire({
          icon: "success",
          title: "Template Updated",
          text: "Your template has been updated successfully.",
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Failed to Update",
        text: "Error while updating template.",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    }
  };

  const markAsDefault = async (templateID = selectedTemplate?.id) => {
    if (!templateID) return;

    try {
      const response = await fetch(`${API_URL}mark-as-default`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": getToken(),
        },
        body: JSON.stringify({
          templateType: "followup",
          projectID: project_id,
          templateID: templateID,
          isDefault: true,
        }),
      });

      if (!response.ok) {
        Swal.fire({
          icon: "error",
          title: "Failed to Add Default",
          text: "Backend error while adding default template.",
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
      // await fetchTemplates();
    } catch (err) {
      console.error("Failed to add as default template:", err);
    }
  };

  const createNewFromExisting = async (makeDefaultAfterCopy = false) => {
    setCreatingNewExisting(true);
    const token = getToken();
    const requestBody = {
      projectID: project_id,
      token: token,
      templateType: "FollowUp",
      subject: subject,
      body: body,
      language: language,
    };
    try {
      const response = await fetch(
        `${API_URL}create-new-from-existing-template`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      const responseData = await response.json();
      if (responseData.status === "success") {
        const copiedTemplateID =
          responseData.templateID ||
          responseData.templateId ||
          responseData.newTemplateID ||
          responseData.id ||
          responseData.template?.id;

        if (makeDefaultAfterCopy && copiedTemplateID) {
          await markAsDefault(copiedTemplateID);
        }

        const refreshedTemplates = await fetchTemplates();

        if (makeDefaultAfterCopy && !copiedTemplateID) {
          const copiedTemplate = (refreshedTemplates || []).find((template) => {
            const sameOwner = template.created_by === userData.username;
            const sameSubject = String(template.subject || "") === String(subject || "");
            const sameBody = String(template.body || "") === String(body || "");
            const sameLanguage = String(template.language || "") === String(language || "");
            return sameOwner && sameSubject && sameBody && sameLanguage;
          });

          if (copiedTemplate?.id) {
            await markAsDefault(copiedTemplate.id);
            await fetchTemplates();
          }
        }
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to fetch project details.",
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch project details.",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    } finally {
      setCreatingNewExisting(false);
    }
  };

  const handleTemplateSelect = (templateID) => {
    const template = templateList.find((t) => String(t.id) === String(templateID));
    if (template) {
      setSelectedTemplate(template);
      setLanguage(template.language);
      setSubject(template.subject || "");
      setBody(template.body || "");
    }
  };

  const handleSetDefault = async () => {
    if (!selectedTemplate) return;
    await markAsDefault();
    await fetchTemplates();
    Swal.fire({
      icon: "success",
      title: "Default Template Updated",
      text: "This template is now your default for this project.",
      timer: 1800,
      timerProgressBar: true,
      showConfirmButton: false,
    });
  };

  const handleSaveAndSetDefault = async () => {
    if (!selectedTemplate) return;
    await handleUpdate({ showSuccess: false });
    await markAsDefault(selectedTemplate.id);
    await fetchTemplates();
    Swal.fire({
      icon: "success",
      title: "Template Saved and Set as Default",
      text: "Your changes were saved and this template is now the default.",
      timer: 2000,
      timerProgressBar: true,
      showConfirmButton: false,
    });
  };

  return (
    <TemplateManagerWorkspace
      title="Follow-up"
      emptyMessage={'Please click on "Create New Template" to create a Follow-up template.'}
      loading={followLoading}
      allTemplates={allTemplates}
      templateList={templateList}
      selectedTemplate={selectedTemplate}
      onSelectTemplate={handleTemplateSelect}
      isIncludeAll={isIncludeAll}
      setIsIncludeAll={setIsIncludeAll}
      userName={userData.username}
      language={language}
      subject={subject}
      body={body}
      setSubject={setSubject}
      setBody={setBody}
      isConnection={false}
      creatingNewExisting={creatingNewExisting}
      onCopyToMine={() => createNewFromExisting(false)}
      onCopyAndSetDefault={() => createNewFromExisting(true)}
      onSaveChanges={handleUpdate}
      onSaveAndSetDefault={handleSaveAndSetDefault}
      onSetDefault={handleSetDefault}
      onReviewTemplate={handleUpdate}
    />
  );
}

export function InviteEmail({ project_id }) {
  const { API_URL } = useContext(UrlContext);
  const { userData } = useContext(UserContext);
  const [allTemplates, setAllTemplates] = useState([]);
  const [templateList, setTemplateList] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [language, setLanguage] = useState("English");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [isIncludeAll, setIsIncludeAll] = useState(true);
  const [creatingNewExisting, setCreatingNewExisting] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);

  const getToken = () => {
    const tokenObj = localStorage.getItem("eph-token");
    return JSON.parse(tokenObj)?.value || "";
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (!isIncludeAll) {
      const filteredTemplates = allTemplates.filter((t) => {
        return t.created_by === userData.username;
      });
      updateTemplateList(filteredTemplates);
    } else {
      updateTemplateList(allTemplates);
    }
  }, [isIncludeAll]);

  const fetchTemplates = async () => {
    setInviteLoading(true);
    try {
      const response = await fetch(
        `${API_URL}get-invite-templates/${project_id}`,
        {
          headers: {
            "x-access-token": getToken(),
          },
        }
      );
      const data = await response.json();

      if (data.status !== "success") {
        Swal.fire({
          icon: "error",
          title: "Failed to Fetch Templates",
          text: "Backend error while fetching outreach templates.",
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }

      const templatesFromDB = data.templates || [];
      const projectDetails = await fetchProjectDetailsForTemplate(API_URL, project_id);
      const fallbackTemplates = buildFallbackTemplateItems("Invite", projectDetails);
      const templatesWithFallbacks = [...templatesFromDB, ...fallbackTemplates];

      setAllTemplates(templatesWithFallbacks);
      updateTemplateList(templatesWithFallbacks);
      return templatesWithFallbacks;
    } catch (err) {
      console.error("Failed to fetch invite templates:", err);
      return [];
    } finally {
      setInviteLoading(false);
    }
  };

  const updateTemplateList = (templates) => {
    if (templates.length > 0) {
      const defaultTemplate =
        templates.find((t) => t.isDefault) || templates[0];
      setSelectedTemplate(defaultTemplate);
      setLanguage(defaultTemplate.language);
      setSubject(defaultTemplate.subject);
      setBody(defaultTemplate.body);
    }
    setTemplateList(templates);
  };

  const handleTemplateChange = (e) => {
    const tpl = templateList.find((t) => String(t.id) === String(e.target.value));
    if (tpl) {
      setSelectedTemplate(tpl);
      setLanguage(tpl.language);
      setSubject(tpl.subject);
      setBody(tpl.body);
    }
  };

  const handleUpdate = async ({ showSuccess = true } = {}) => {
    if (!selectedTemplate) return alert("No template selected to update.");

    try {
      const response = await fetch(
        `${API_URL}update-invite-template/${selectedTemplate.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "x-access-token": getToken(),
          },
          body: JSON.stringify({
            language,
            subject,
            body,
          }),
        }
      );

      if (!response.ok) {
        Swal.fire({
          icon: "error",
          title: "Failed to Update",
          text: "Backend error while updating template.",
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
      await fetchTemplates();
      if (showSuccess) {
        Swal.fire({
          icon: "success",
          title: "Template Updated",
          text: "Your template has been updated successfully.",
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Failed to Update",
        text: "Error while updating template.",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    }
  };

  const markAsDefault = async (templateID = selectedTemplate?.id) => {
    if (!templateID) return;

    try {
      const response = await fetch(`${API_URL}mark-as-default`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": getToken(),
        },
        body: JSON.stringify({
          templateType: "invite",
          projectID: project_id,
          templateID: templateID,
          isDefault: true,
        }),
      });

      if (!response.ok) {
        Swal.fire({
          icon: "error",
          title: "Failed to Add Default",
          text: "Backend error while adding default template.",
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
      // await fetchTemplates();
    } catch (err) {
      console.error("Failed to add as default template:", err);
    }
  };

  const createNewFromExisting = async (makeDefaultAfterCopy = false) => {
    setCreatingNewExisting(true);
    const token = getToken();
    const requestBody = {
      projectID: project_id,
      token: token,
      templateType: "Invite",
      subject: subject,
      body: body,
      language: language,
    };
    try {
      const response = await fetch(
        `${API_URL}create-new-from-existing-template`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      const responseData = await response.json();
      if (responseData.status === "success") {
        const copiedTemplateID =
          responseData.templateID ||
          responseData.templateId ||
          responseData.newTemplateID ||
          responseData.id ||
          responseData.template?.id;

        if (makeDefaultAfterCopy && copiedTemplateID) {
          await markAsDefault(copiedTemplateID);
        }

        const refreshedTemplates = await fetchTemplates();

        if (makeDefaultAfterCopy && !copiedTemplateID) {
          const copiedTemplate = (refreshedTemplates || []).find((template) => {
            const sameOwner = template.created_by === userData.username;
            const sameSubject = String(template.subject || "") === String(subject || "");
            const sameBody = String(template.body || "") === String(body || "");
            const sameLanguage = String(template.language || "") === String(language || "");
            return sameOwner && sameSubject && sameBody && sameLanguage;
          });

          if (copiedTemplate?.id) {
            await markAsDefault(copiedTemplate.id);
            await fetchTemplates();
          }
        }
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to fetch project details.",
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch project details.",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    } finally {
      setCreatingNewExisting(false);
    }
  };

  const handleTemplateSelect = (templateID) => {
    const template = templateList.find((t) => String(t.id) === String(templateID));
    if (template) {
      setSelectedTemplate(template);
      setLanguage(template.language);
      setSubject(template.subject || "");
      setBody(template.body || "");
    }
  };

  const handleSetDefault = async () => {
    if (!selectedTemplate) return;
    await markAsDefault();
    await fetchTemplates();
    Swal.fire({
      icon: "success",
      title: "Default Template Updated",
      text: "This template is now your default for this project.",
      timer: 1800,
      timerProgressBar: true,
      showConfirmButton: false,
    });
  };

  const handleSaveAndSetDefault = async () => {
    if (!selectedTemplate) return;
    await handleUpdate({ showSuccess: false });
    await markAsDefault(selectedTemplate.id);
    await fetchTemplates();
    Swal.fire({
      icon: "success",
      title: "Template Saved and Set as Default",
      text: "Your changes were saved and this template is now the default.",
      timer: 2000,
      timerProgressBar: true,
      showConfirmButton: false,
    });
  };

  return (
    <TemplateManagerWorkspace
      title="Invite"
      emptyMessage={'Please click on "Create New Template" to create an Invite template.'}
      loading={inviteLoading}
      allTemplates={allTemplates}
      templateList={templateList}
      selectedTemplate={selectedTemplate}
      onSelectTemplate={handleTemplateSelect}
      isIncludeAll={isIncludeAll}
      setIsIncludeAll={setIsIncludeAll}
      userName={userData.username}
      language={language}
      subject={subject}
      body={body}
      setSubject={setSubject}
      setBody={setBody}
      isConnection={false}
      creatingNewExisting={creatingNewExisting}
      onCopyToMine={() => createNewFromExisting(false)}
      onCopyAndSetDefault={() => createNewFromExisting(true)}
      onSaveChanges={handleUpdate}
      onSaveAndSetDefault={handleSaveAndSetDefault}
      onSetDefault={handleSetDefault}
      onReviewTemplate={handleUpdate}
    />
  );
}

export function ConnectionNote({ project_id }) {
  const { API_URL } = useContext(UrlContext);
  const { userData } = useContext(UserContext);
  const [allTemplates, setAllTemplates] = useState([]);
  const [templateList, setTemplateList] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [language, setLanguage] = useState("English");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [isIncludeAll, setIsIncludeAll] = useState(true);
  const [creatingNewExisting, setCreatingNewExisting] = useState(false);
  const [connectionLoading, setConnectionLoading] = useState(false);

  const getToken = () => {
    const tokenObj = localStorage.getItem("eph-token");
    return JSON.parse(tokenObj)?.value || "";
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (!isIncludeAll) {
      const filteredTemplates = allTemplates.filter((t) => {
        return t.created_by === userData.username;
      });
      updateTemplateList(filteredTemplates);
    } else {
      updateTemplateList(allTemplates);
    }
  }, [isIncludeAll]);

  const fetchTemplates = async () => {
    setConnectionLoading(true);
    try {
      const response = await fetch(
        `${API_URL}get-connection-templates/${project_id}`,
        {
          headers: {
            "x-access-token": getToken(),
          },
        }
      );
      const data = await response.json();

      if (data.status !== "success") {
        Swal.fire({
          icon: "error",
          title: "Failed to Fetch Templates",
          text: "Backend error while fetching outreach templates.",
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }

      const templatesFromDB = data.templates || [];
      const projectDetails = await fetchProjectDetailsForTemplate(API_URL, project_id);
      const fallbackTemplates = buildFallbackTemplateItems("Connection", projectDetails);
      const templatesWithFallbacks = [...templatesFromDB, ...fallbackTemplates];

      setAllTemplates(templatesWithFallbacks);
      updateTemplateList(templatesWithFallbacks);
      return templatesWithFallbacks;
    } catch (err) {
      console.error("Failed to fetch invite templates:", err);
      return [];
    } finally {
      setConnectionLoading(false);
    }
  };

  const updateTemplateList = (templates) => {
    if (templates.length > 0) {
      const defaultTemplate =
        templates.find((t) => t.isDefault) || templates[0];
      setSelectedTemplate(defaultTemplate);
      setLanguage(defaultTemplate.language);
      setBody(defaultTemplate.body);
    }
    setTemplateList(templates);
  };

  const handleTemplateChange = (e) => {
    const tpl = templateList.find((t) => String(t.id) === String(e.target.value));
    if (tpl) {
      setSelectedTemplate(tpl);
      setLanguage(tpl.language);
      setBody(tpl.body);
    }
  };

  const handleUpdate = async ({ showSuccess = true } = {}) => {
    if (!selectedTemplate) return alert("No template selected to update.");

    try {
      const response = await fetch(
        `${API_URL}update-connection-template/${selectedTemplate.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "x-access-token": getToken(),
          },
          body: JSON.stringify({
            language,
            subject,
            body,
          }),
        }
      );

      if (!response.ok) {
        Swal.fire({
          icon: "error",
          title: "Failed to Update",
          text: "Backend error while updating template.",
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
      await fetchTemplates();
      if (showSuccess) {
        Swal.fire({
          icon: "success",
          title: "Template Updated",
          text: "Your template has been updated successfully.",
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Failed to Update",
        text: "Error while updating template.",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    }
  };

  const markAsDefault = async (templateID = selectedTemplate?.id) => {
    if (!templateID) return;

    try {
      const response = await fetch(`${API_URL}mark-as-default`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": getToken(),
        },
        body: JSON.stringify({
          templateType: "connection",
          projectID: project_id,
          templateID: templateID,
          isDefault: true,
        }),
      });

      if (!response.ok) {
        Swal.fire({
          icon: "error",
          title: "Failed to Add Default",
          text: "Backend error while adding default template.",
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
      // await fetchTemplates();
    } catch (err) {
      console.error("Failed to add as default template:", err);
    }
  };

  const createNewFromExisting = async (makeDefaultAfterCopy = false) => {
    setCreatingNewExisting(true);
    const token = getToken();
    const requestBody = {
      projectID: project_id,
      token: token,
      templateType: "Connection",
      subject: subject,
      body: body,
      language: language,
    };
    try {
      const response = await fetch(
        `${API_URL}create-new-from-existing-template`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      const responseData = await response.json();
      if (responseData.status === "success") {
        const copiedTemplateID =
          responseData.templateID ||
          responseData.templateId ||
          responseData.newTemplateID ||
          responseData.id ||
          responseData.template?.id;

        if (makeDefaultAfterCopy && copiedTemplateID) {
          await markAsDefault(copiedTemplateID);
        }

        const refreshedTemplates = await fetchTemplates();

        if (makeDefaultAfterCopy && !copiedTemplateID) {
          const copiedTemplate = (refreshedTemplates || []).find((template) => {
            const sameOwner = template.created_by === userData.username;
            const sameSubject = String(template.subject || "") === String(subject || "");
            const sameBody = String(template.body || "") === String(body || "");
            const sameLanguage = String(template.language || "") === String(language || "");
            return sameOwner && sameSubject && sameBody && sameLanguage;
          });

          if (copiedTemplate?.id) {
            await markAsDefault(copiedTemplate.id);
            await fetchTemplates();
          }
        }
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to fetch project details.",
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch project details.",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    } finally {
      setCreatingNewExisting(false);
    }
  };

  const handleTemplateSelect = (templateID) => {
    const template = templateList.find((t) => String(t.id) === String(templateID));
    if (template) {
      setSelectedTemplate(template);
      setLanguage(template.language);
      setSubject(template.subject || "");
      setBody(template.body || "");
    }
  };

  const handleSetDefault = async () => {
    if (!selectedTemplate) return;
    await markAsDefault();
    await fetchTemplates();
    Swal.fire({
      icon: "success",
      title: "Default Template Updated",
      text: "This template is now your default for this project.",
      timer: 1800,
      timerProgressBar: true,
      showConfirmButton: false,
    });
  };

  const handleSaveAndSetDefault = async () => {
    if (!selectedTemplate) return;
    await handleUpdate({ showSuccess: false });
    await markAsDefault(selectedTemplate.id);
    await fetchTemplates();
    Swal.fire({
      icon: "success",
      title: "Template Saved and Set as Default",
      text: "Your changes were saved and this template is now the default.",
      timer: 2000,
      timerProgressBar: true,
      showConfirmButton: false,
    });
  };

  return (
    <TemplateManagerWorkspace
      title="Connection note"
      emptyMessage={'Please click on "Create New Template" to create a Connection Note template.'}
      loading={connectionLoading}
      allTemplates={allTemplates}
      templateList={templateList}
      selectedTemplate={selectedTemplate}
      onSelectTemplate={handleTemplateSelect}
      isIncludeAll={isIncludeAll}
      setIsIncludeAll={setIsIncludeAll}
      userName={userData.username}
      language={language}
      subject={subject}
      body={body}
      setSubject={setSubject}
      setBody={setBody}
      isConnection={true}
      creatingNewExisting={creatingNewExisting}
      onCopyToMine={() => createNewFromExisting(false)}
      onCopyAndSetDefault={() => createNewFromExisting(true)}
      onSaveChanges={handleUpdate}
      onSaveAndSetDefault={handleSaveAndSetDefault}
      onSetDefault={handleSetDefault}
      onReviewTemplate={handleUpdate}
    />
  );
}